import type { BankCovenantItem } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { formatDateRu, formatMillions } from '../financierFormat';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionHeaderSub,
  CovenantsGrid,
  CovenantCard,
  CovenantHeader,
  BankName,
  CovenantType,
  CovenantTurnoverRow,
  ProgressBarWrap,
  ProgressBarFill,
  CovenantMeta,
  EmptyCovenantsCard,
} from './styled';

interface CovenantsSectionProps {
  covenants: BankCovenantItem[] | undefined;
}

export function CovenantsSection({ covenants }: CovenantsSectionProps) {
  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Мониторинг банковских ковенант</SectionTitle>
        <SectionHeaderSub>
          Из карточек кредитов и лизинга: вкладка «Кредиты и лизинг»
        </SectionHeaderSub>
      </SectionHeader>
      <CovenantsGrid>
        {covenants && covenants.length > 0 ? (
          covenants.map((cov, idx) => {
            // «В норме» — только если соблюдение оценено (есть расчёт выполнения);
            // статус «ok» из карточки кредита ставится по умолчанию и ничего не значит
            const assessed = cov.compliance_pct !== null;
            const statusTone =
              cov.status === 'breach'
                ? 'danger'
                : cov.status === 'warning'
                  ? 'warning'
                  : assessed
                    ? 'success'
                    : 'neutral';
            const statusLabel =
              cov.status === 'breach'
                ? 'Нарушение'
                : cov.status === 'warning'
                  ? 'Внимание'
                  : assessed
                    ? 'В норме'
                    : 'Не оценивался';
            return (
              <CovenantCard
                key={idx}
                $status={cov.status === 'ok' && !assessed ? 'unassessed' : cov.status}
              >
                <CovenantHeader>
                  <div>
                    <BankName>{cov.bank_name}</BankName>
                    <CovenantType>{cov.covenant_name}</CovenantType>
                  </div>
                  <Badge $tone={statusTone}>{statusLabel}</Badge>
                </CovenantHeader>
                {cov.actual_turnover_month !== null && cov.required_turnover_month !== null && (
                  <div>
                    <CovenantTurnoverRow>
                      <span>Факт: <b>{formatMillions(cov.actual_turnover_month)}</b></span>
                      <span>Требуется: {formatMillions(cov.required_turnover_month)}</span>
                    </CovenantTurnoverRow>
                    {cov.compliance_pct !== null && (
                      <ProgressBarWrap>
                        <ProgressBarFill $pct={cov.compliance_pct} $status={cov.status} />
                      </ProgressBarWrap>
                    )}
                  </div>
                )}
                {(cov.compliance_pct !== null || cov.deadline) && (
                  <CovenantMeta>
                    {cov.compliance_pct !== null && (
                      <span>Выполнение: <b>{cov.compliance_pct.toFixed(1)}%</b></span>
                    )}
                    {cov.deadline && <span>Контрольный срок: {formatDateRu(cov.deadline)}</span>}
                  </CovenantMeta>
                )}
              </CovenantCard>
            );
          })
        ) : (
          <EmptyCovenantsCard>
            Ковенантов нет: их заносят в карточку кредита или лизинга (поле «Ковенанты банка»).
          </EmptyCovenantsCard>
        )}
      </CovenantsGrid>
    </Section>
  );
}
